import React, { useContext, useEffect, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { useRouteMatch } from 'react-router-dom';
import * as T from 'prop-types';
import _ from 'lodash';

import ConsultantInfoPage from './ConsultantInfoPage';
import TermsOfParticipationOnboardingPage from './TermsOfParticipationOnboardingPage';
import SlidePresentationPage from './SlidePresentationPage';
import OversightQuestionnairePage from './oversight/OversightQuestionnairePage';
import { formStatusesByCurrentPage } from './FormStatus';
import { AuthContext } from '../../commons/auth/AuthContext';
import LoadingPage from '../../commons/LoadingPage';
import { useAutoSaveDraft } from '../../commons/draftHooks';
import {
    fetchDraft,
    fetchOQs,
    fetchProjectCompanies,
    saveProgress,
    submitApplication,
    updateStatus,
    logOnboardingUserAgentInfo,
    isPreviousTrainingValid,
    markTrainingCompletion,
    getToPDigitalSignatureContract,
} from '../onboardingRoutes';
import { getPlatformInfo } from '../../commons/UserAgentInfo';
import { PrivacyPolicyOnboardingPage } from './PrivacyPolicyOnboardingPage';
import DisclaimerChecksPopUp from './DisclaimerChecksPopup';

const _submit = (url, formValues, authContext) => {
    const obj = _.merge(
        {},
        formValues.disclaimerChecks?.values,
        formValues.infoPage.values,
        formValues.termsPage.values,
        formValues.privacyPage.values,
        formValues.slidesPage.values,
        formValues.oqPage.values
    );

    return new Promise((resolve, reject) => {
        submitApplication(url, obj, authContext)
            .then(() => resolve({}))
            .catch((error) => {
                resolve(error);
            });
    });
};

const _updateStatus = (url, status, authContext) => {
    updateStatus(url, { status }, authContext).catch(console.error);
};

function _saveField(input, values, errors) {
    input.onChange({ values, errors });
}

// eslint-disable-next-line no-unused-vars
export const OnboardingForm = (props) => {
    const [OQs, setOQs] = useState([]);
    const [draft, setDraft] = useState({});
    const [projectCompanies, setProjectCompanies] = useState([]);

    const defaultSlidesWatched = 1;
    // slides watched indicates, slides watched in its completeness without skipping.
    const [slidesWatched, setSlidesWatched] = useState(defaultSlidesWatched);

    const [currentPage, setCurrentPage] = useState(props.initialPageNumber);

    const [hasCompletedTrainingPreviously, setCompletedTrainingPreviously] =
        useState(false);

    const initialValues = draft.values;
    const authContext = useContext(AuthContext);
    const routeMatch = useRouteMatch();
    const { url } = routeMatch.params;

    useEffect(() => {
        setSlidesWatched(draft?.meta?.slidesWatched ?? defaultSlidesWatched);
    }, [draft?.meta?.slidesWatched]);

    // execute effect on first render.
    useEffect(() => {
        fetchDraft(url, authContext, setDraft);
        getToPDigitalSignatureContract(url, authContext);
        fetchOQs(url, authContext, setOQs);
        fetchProjectCompanies(url, authContext, setProjectCompanies);
        isPreviousTrainingValid(url, authContext, ({ isValid }) =>
            setCompletedTrainingPreviously(isValid)
        );
        logOnboardingUserAgentInfo(
            url,
            getPlatformInfo('app.onboardForm'),
            authContext
        ).catch(console.error);
    }, []);

    const _next = (input, values) => {
        _saveField(input, values, {});

        // update status whenever next page is called on First (Basic Info) or Fourth page (Slides).
        if (currentPage === 1 || currentPage === 4) {
            _updateStatus(
                url,
                formStatusesByCurrentPage[currentPage + 1],
                authContext
            );
        }

        setCurrentPage(currentPage + 1);
        window.scrollTo(0, 0);
    };

    const _prev = (input, values, errors) => {
        _saveField(input, values, errors);
        setCurrentPage((value) => value - 1);
        window.scrollTo(0, 0);
    };
    if (!initialValues) {
        return (
            <div>
                <LoadingPage />
            </div>
        );
    }

    return (
        <Form
            onSubmit={(values) => _submit(url, values, authContext)}
            initialValues={initialValues}
            render={({
                values,
                form,
                submitting,
                submitSucceeded,
                submitErrors,
            }) => {
                // default autoSaveInterval value is 2 seconds. Here sending draft to server at every 10 seconds.
                useAutoSaveDraft(
                    (formValues, errors, additionalDeps) => {
                        // do not forget to use the updated dependencies provided by callback fn.
                        // instead of reading them from global scope. (Refer function doc for more details)
                        const [lAuthContext, lSlidesWatched, lCurrentPage] =
                            additionalDeps;
                        // eslint-disable-next-line no-unused-expressions
                        !lAuthContext?.auth?.isExpired
                            ? saveProgress(
                                  url,
                                  {
                                      values: formValues,
                                      meta: {
                                          slidesWatched: lSlidesWatched,
                                          currentPage: lCurrentPage,
                                      },
                                  },
                                  lAuthContext
                              )
                            : _.noop();
                    },
                    values,
                    undefined,
                    [authContext, slidesWatched, currentPage],
                    10_000
                );

                // submitSucceeded or submitErrors changes
                useEffect(() => {
                    props.setSubmitSucceeded(submitSucceeded);
                    props.setSubmitErrors(submitErrors);
                }, [submitSucceeded, submitErrors]);

                return (
                    <div className="max-w-4xl mx-auto rounded text-sm">
                        {submitting && (
                            <LoadingPage
                                message={'Submitting Application...'}
                            />
                        )}
                        {
                            <Field
                                name={'disclaimerChecks'}
                                render={({ input }) => (
                                    <DisclaimerChecksPopUp
                                        initialValues={input.value?.values}
                                        consultantCountry={
                                            props.consultantCountry
                                        }
                                        autoSaveCallback={(
                                            formValues,
                                            formErrors
                                        ) => {
                                            _saveField(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                    />
                                )}
                            />
                        }
                        {currentPage === 1 && (
                            <Field
                                name={'infoPage'}
                                render={({ input }) => (
                                    <ConsultantInfoPage
                                        initialValues={input.value?.values}
                                        consultantCountry={
                                            props.consultantCountry
                                        }
                                        autoSaveCallback={(
                                            formValues,
                                            formErrors
                                        ) => {
                                            _saveField(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                        nextFn={(formValues) => {
                                            _next(input, formValues);
                                        }}
                                    />
                                )}
                            />
                        )}

                        {currentPage === 2 && (
                            <Field
                                name={'privacyPage'}
                                render={({ input }) => (
                                    <PrivacyPolicyOnboardingPage
                                        initialValues={input.value?.values}
                                        country={props.consultantCountry}
                                        autoSaveCallback={(
                                            formValues,
                                            formErrors
                                        ) => {
                                            _saveField(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                        nextFn={(formValues) => {
                                            _next(input, formValues);
                                        }}
                                        prevFn={(formValues, formErrors) => {
                                            _prev(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                    />
                                )}
                            />
                        )}

                        {currentPage === 3 && (
                            <Field
                                name={'termsPage'}
                                render={({ input }) => (
                                    <TermsOfParticipationOnboardingPage
                                        initialValues={input.value?.values}
                                        country={props.consultantCountry}
                                        autoSaveCallback={(
                                            formValues,
                                            formErrors
                                        ) => {
                                            _saveField(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                        nextFn={(formValues) => {
                                            _next(input, formValues);
                                        }}
                                        prevFn={(formValues, formErrors) => {
                                            _prev(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                    />
                                )}
                            />
                        )}

                        {currentPage === 4 && (
                            <Field
                                name={'slidesPage'}
                                render={({ input }) => (
                                    <SlidePresentationPage
                                        initialValues={input.value?.values}
                                        country={props.consultantCountry}
                                        autoSaveCallback={(
                                            formValues,
                                            formErrors
                                        ) => {
                                            _saveField(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                        nextFn={(formValues) => {
                                            _next(input, formValues);
                                        }}
                                        prevFn={(formValues, formErrors) => {
                                            _prev(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                        onTrainingCompletion={() => {
                                            markTrainingCompletion(
                                                url,
                                                authContext
                                            );
                                        }}
                                        hasCompletedTrainingPreviously={
                                            hasCompletedTrainingPreviously
                                        }
                                        slidesWatched={slidesWatched}
                                        setSlidesWatched={setSlidesWatched}
                                    />
                                )}
                            />
                        )}

                        {currentPage === 5 && (
                            <Field
                                name={'oqPage'}
                                render={({ input }) => (
                                    <OversightQuestionnairePage
                                        autoSaveCallback={(
                                            formValues,
                                            formErrors
                                        ) => {
                                            _saveField(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                        initialValues={input.value?.values}
                                        nextFn={(formValues) => {
                                            _saveField(input, formValues, {});
                                            form.submit();
                                        }}
                                        prevFn={(formValues, formErrors) => {
                                            _prev(
                                                input,
                                                formValues,
                                                formErrors
                                            );
                                        }}
                                        oqs={OQs}
                                        projectCompanies={projectCompanies}
                                        disableSubmit={
                                            submitting || submitSucceeded
                                        }
                                    />
                                )}
                            />
                        )}
                        <div className={'block text-center'}>
                            Page {currentPage} of 5
                        </div>
                        {/* <pre>{JSON.stringify(values, undefined, 2)}</pre> */}
                    </div>
                );
            }}
        />
    );
};

OnboardingForm.propTypes = {
    setSubmitSucceeded: T.func,
    setSubmitErrors: T.func,
    consultantCountry: T.string,
    initialPageNumber: T.number,
};
